<!-- This component is used to render the supplemental steps inside of the plan holder dropdown in the left side bar, showing a file icon. -->
<template>
	<Disclosure
		:defaultOpen="defaultOpen"
		v-slot="{ open }"
	>
		<DisclosureButton
			:id="plan.id + '-supplemental-steps-toggle'"
			class="flex items-center justify-between px-4 py-2 text-left"
			:aria-label="t('document.supplementalFormsToggle')"
			:disabled="disabled"
		>
			<div
				class="flex items-center gap-3 py-2"
				:class="{
					'text-secondary-900!': !disabled,
					'text-secondary-400!': disabled,
				}"
			>
				<DocumentTextIcon class="h-8 w-8" />
				<span
					v-if="expanded"
					class="animate-fade-in-delay text-sm font-bold uppercase opacity-0"
				>
					{{ t('document.supplementalForms') }}
				</span>
			</div>

			<ChevronRightIcon
				v-if="expanded && !disabled"
				:class="{ 'rotate-90 transform': open }"
				class="h-5 w-5 text-xs text-slate-500! transition-transform duration-200"
			/>
		</DisclosureButton>
		<DisclosurePanel
			class="flex flex-col text-gray-500!"
			:class="{ 'items-center': !expanded }"
			:aria-label="t('document.supplementalSteps')"
		>
			<slot v-if="!disabled" />
		</DisclosurePanel>
	</Disclosure>
</template>

<script setup lang="ts">
import Plan from '@/classes/Plan'
import { usePresentationLocale } from '@/i18n'
import { useSessionDocumentsStore } from '@/stores/sessionDocuments'
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/vue'
import { DocumentTextIcon } from '@heroicons/vue/24/outline'
import { ChevronRightIcon } from '@heroicons/vue/24/solid'
import { storeToRefs } from 'pinia'
import { watch, ref } from 'vue'

const { t } = usePresentationLocale()
const sessionDocumentsStore = useSessionDocumentsStore()
const { pageState } = storeToRefs(sessionDocumentsStore)

watch(pageState, (newPageState) => {
	if (newPageState.status === 'complete') {
		setTimeout(() => {
			openSupplementalDropdown()
		}, 100)
	}
})

const openSupplementalDropdown = () => {
	const dropdownOpen = document.querySelector(
		'[aria-label="planholder-supplemental-steps-toggle"][data-headlessui-state=open]'
	)
	if (dropdownOpen) return

	const selector = `${props.plan.id}-supplemental-steps-toggle`
	if (!selector) return

	const element = document.getElementById(selector)
	if (element) element.dispatchEvent(new Event('click'))
}

const props = defineProps<{
	plan: Plan
	index: number
	defaultOpen?: boolean
	expanded: boolean
	disabled: boolean
}>()
</script>
